/**
 * Try.tsx — Pre-auth free industry insight tool (Story 19 PLG)
 *
 * v1.0 - 2026-03-16 - Story 19: Public /try page. User types their industry and gets one
 *                     AI-generated teaser question that MyLojic would answer for their business.
 *                     Optional email to receive the question by email.
 *                     CTA: "See the answer → Start your free demo" → /demo-register
 * v4.35 - 2026-10-29 - PILOT-GATE-BROWSER-ONLY-01 (row 737, Branko ruling 2026-10-28): the demo funnel is closed;
 *                     the CTA goes to "/" instead of /demo-register. Nothing else on the page changes.
 *
 * No authentication required. Visible to anyone.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import { portalRequestUngated } from '../services/portalRequest'; // v4.28 - G10-B: one request wrapper, carries the chosen company

type PageState = 'idle' | 'loading' | 'result' | 'error';

interface TryResult {
  question: string;
  naics_code: string;
  industry_label: string;
}

const Try: React.FC = () => {
  const navigate = useNavigate();
  const [industry, setIndustry] = useState('');
  const [email, setEmail] = useState('');
  const [pageState, setPageState] = useState<PageState>('idle');
  const [result, setResult] = useState<TryResult | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!industry.trim()) return;
    setPageState('loading');
    setErrorMsg('');
    try {
      const body: Record<string, string> = { industry: industry.trim() };
      if (email.trim()) body.email = email.trim().toLowerCase();

      const res = await portalRequestUngated('/api/try/question', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.question) {
        setResult(data as TryResult);
        setPageState('result');
      } else {
        setErrorMsg(data.detail || data.error || 'Something went wrong. Please try again.');
        setPageState('error');
      }
    } catch {
      setErrorMsg('Network error. Please try again.');
      setPageState('error');
    }
  };

  const handleReset = () => {
    setIndustry('');
    setEmail('');
    setResult(null);
    setErrorMsg('');
    setPageState('idle');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 6, sm: 10 },
      }}
    >
      <Container maxWidth="sm">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, mb: 1.5, letterSpacing: '-0.5px' }}
          >
            See what MyLojic would tell your business
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Type your industry and we'll show you the kind of question we answer automatically.
            No signup needed.
          </Typography>
        </Box>

        {/* Result card */}
        {pageState === 'result' && result && (
          <Paper
            elevation={4}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              mb: 3,
              border: '2px solid',
              borderColor: 'primary.main',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <LightbulbIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Here's the kind of question MyLojic answers for a{' '}
                <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
                  {result.industry_label}
                </Box>
                :
              </Typography>
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                lineHeight: 1.4,
                fontStyle: 'italic',
                color: 'text.primary',
                mb: 3,
                pl: 2,
                borderLeft: '4px solid',
                borderColor: 'primary.light',
              }}
            >
              "{result.question}"
            </Typography>

            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/') /* v4.35 - PILOT-GATE-BROWSER-ONLY-01: was /demo-register (closed) */}
              fullWidth
              sx={{ fontWeight: 700, textTransform: 'none', py: 1.5, mb: 1.5, fontSize: '1rem' }}
            >
              See the answer with your real data
            </Button>

            <Button
              variant="text"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Try a different industry
            </Button>
          </Paper>
        )}

        {/* Input form — shown on idle + error, hidden on result */}
        {pageState !== 'result' && (
          <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
            {pageState === 'error' && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorMsg}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                label="What type of business do you run?"
                placeholder="e.g. residential roofing, restaurant, law firm, plumbing contractor..."
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                required
                fullWidth
                autoFocus
                disabled={pageState === 'loading'}
                sx={{ mb: 2 }}
              />

              {/* v1.0 Correction 5: optional email field */}
              <TextField
                label="Get your result by email too (optional)"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                disabled={pageState === 'loading'}
                sx={{ mb: 3 }}
                helperText="We'll send you the question. No spam, ever."
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={pageState === 'loading' || !industry.trim()}
                sx={{ fontWeight: 700, textTransform: 'none', py: 1.5, fontSize: '1rem' }}
              >
                {pageState === 'loading' ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Show me an example insight'
                )}
              </Button>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 2 }}
            >
              Already have an account?{' '}
              <Link
                component="button"
                variant="caption"
                onClick={() => navigate('/login')}
                sx={{ cursor: 'pointer' }}
              >
                Sign in
              </Link>
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default Try;
